import { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import type { ReceiptData } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { longDt } from '../lib/filters';

/**
 * Účtenka — the legacy `printreceive`, as a page laid out for the small
 * printer at the desk. Opening it issues the number (first time) or
 * re-reads it (every later time), then prints itself, as the legacy popup
 * did; `?tisk=0` previews without printing. Field for field the legacy
 * receipt: supplier, customer when invoiced, the tax date, the number, the
 * booking, from/to, the payment, the lines, the total, the prepaid
 * deductions, the amount to pay, and the VAT breakdown.
 */
const kc = (n: number) => `${CZK.format(n)} Kč`;
const kc2 = (n: number) => `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;

export function Uctenka() {
  const { id } = useParams();
  const rid = Number(id);
  const [sp] = useSearchParams();
  const preview = sp.get('tisk') === '0';
  const m = useMutation({ mutationFn: () => api.receipt(rid) });
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !Number.isInteger(rid) || rid <= 0) return;
    fired.current = true;
    m.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);
  useEffect(() => {
    if (!m.data || preview) return;
    const t = window.setTimeout(() => window.print(), 250);
    return () => window.clearTimeout(t);
  }, [m.data, preview]);

  if (!Number.isInteger(rid) || rid <= 0) return <div className="container state">Neplatné číslo rezervace.</div>;
  if (m.isPending || m.isIdle) return <div className="container state">Připravuji účtenku…</div>;
  if (m.isError || !m.data) {
    const e = m.error;
    const msg = e instanceof ApiError ? e.serverMessage ?? e.message : 'Účtenku se nepodařilo vystavit.';
    return <div className="container state">{msg}</div>;
  }
  const d = m.data as ReceiptData;
  const r = d.reservation;
  const invoiced = r.paidInvoice > 0;

  return (
    <div className="protokol uctenka">
      <div className="controls noprint">
        <button type="button" className="btn" onClick={() => window.print()}>
          {preview ? 'Tisk' : 'Opakovat tisk'}
        </button>
        <button type="button" className="btn" onClick={() => window.close()}>
          Zavřít okno
        </button>
      </div>
      <div className="sheet">
        <p className="center small">Staňte se členem Smile Parking Clubu! www.smileparking.cz</p>
        <div className="grid2">
          <div>
            <p>
              <strong>Dodavatel:</strong>
              <br />
              Smile Parking s.r.o.
              <br />
              U Špejcharu 504, Tuchoměřice, 252 67
              <br />
              IČ: 09051368, DIČ: CZ09051368
            </p>
          </div>
          <div className="right">
            {r.invoiceCompany && (
              <p>
                <strong>Odběratel:</strong>
                <br />
                {r.invoiceCompany}
                <br />
                {[r.invoiceStreet, r.invoiceCity, r.invoicePsc].filter(Boolean).join(', ')}
                <br />
                {r.invoiceIc ? `IČ: ${r.invoiceIc}` : ''}
                {r.invoiceDic ? ` DIČ: ${r.invoiceDic}` : ''}
              </p>
            )}
            <p>
              Datum (DUZP): {longDt(d.taxDate)}
              <br />
              Vystavil: {d.issuedBy}
            </p>
            <h2>Účtenka č.: {d.number}</h2>
          </div>
        </div>
        <p>
          ID rezervace: <strong>{r.id}</strong>
          <br />
          Parkování od: {longDt(r.arrivalReal ?? r.arrival)}
          <br />
          Parkování do: {longDt(r.departureReal ?? r.departure)}
          <br />
          Platba: {d.paymentLabel}
        </p>
        <table>
          <thead>
            <tr>
              <th>Popis</th>
              <th className="num">DPH</th>
              <th className="num">Cena včetně DPH</th>
            </tr>
          </thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.title}</td>
                <td className="num">21 %</td>
                <td className="num">{kc(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2}>Celkem:</th>
              <td className="num">{kc(d.total)}</td>
            </tr>
            {d.prepaid.map((l, i) => (
              <tr key={`p${i}`}>
                <td colSpan={2}>{l.title}:</td>
                <td className="num">{kc(l.amount)}</td>
              </tr>
            ))}
            {d.toPay !== d.total && (
              <tr>
                <th colSpan={2}>K úhradě:</th>
                <td className="num">{kc(d.toPay)}</td>
              </tr>
            )}
          </tfoot>
        </table>
        <table className="vat">
          <caption>Vyčíslení DPH</caption>
          <thead>
            <tr>
              <th>Sazba</th>
              <th className="num">Základ</th>
              <th className="num">DPH</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{d.vat.rate} %</td>
              <td className="num">{kc2(d.vat.base)}</td>
              <td className="num">{kc2(d.vat.vat)}</td>
            </tr>
          </tbody>
        </table>
        {invoiced && <p className="small">Hrazeno partnerem (fakturace CK).</p>}
        <p className="center">Těšíme se na Vaši příští návštěvu u Smile Parkingu.</p>
      </div>
    </div>
  );
}
